/**
  GRAPHQL
 */
import { v4 as uuidv4 } from 'uuid';

import {
  UNAUTHORIZED_MESSAGE,
  GQL_FEDERATION_FEATURE_OPTIMIZELY_KEY,
  GQL_FEDERATION_QUERIES_AND_MUTATIONS_FEATURE_VARIABLE_OPTIMIZELY_KEY,
} from '../constants';
import { getFeatureVariable } from '../utilities/feature-flags';
import {
  getJWTFromLocalStorage,
  removeJWTFromLocalStorage,
} from '../utilities/jwt';
import {
  logError,
  heapTrackClientError,
  heapTrackUnauthorized,
} from '../utilities/logging';

export interface CreditCardLegalDocument {
  name: string;
  title?: string;
  version: number;
  viewUrl?: string;
}

export interface ApplicationLegalDocument {
  classification: string;
  createdDate: string;
  documentId: string;
  signedDownloadUrl: string;
  signedViewUrl: string;
  title: string;
}

interface GetCreditCardLegalDocuments {
  getCreditCardLegalDocuments?: CreditCardLegalDocument[];
}

interface GraphQLResponseError {
  code?: string;
  message?: string;
}

export interface GraphQLResponse {
  data?: GetCreditCardLegalDocuments | any;
  errors?: Array<GraphQLResponseError>;
}

interface StringMap {
  [key: string]: string;
}

const graphqlDomains: StringMap = {
  localhost: 'https://simplicity.qa32.circleone.com/graphql/',
  '0.0.0.0': 'https://simplicity.qa32.circleone.com/graphql/',
  '127.0.0.1': 'http://127.0.0.1:8000/',
  'local.prosper': 'http://www.prosper.local/graphql/',
  'www.prosper.local': 'http://www.prosper.local/graphql/',
  'www.qa32.c1.qa': 'https://simplicity.qa32.circleone.com/graphql/',
  'www.qa32.circleone.com': 'https://simplicity.qa32.circleone.com/graphql/',
  'www.k8s-simplicity.qa32.circleone.com':
    'https://k8s-simplicity.qa32.circleone.com/graphql/',
  'www.stage.circleone.com': 'https://simplicity.stage.circleone.com/graphql/',
  'www.k8s-simplicity.stage.circleone.com':
    'https://k8s-simplicity.stage.circleone.com/graphql/',
  'www.uat.circleone.com': 'https://simplicity.uat.circleone.com/graphql/',
  'www.k8s-simplicity.uat.circleone.com':
    'https://k8s-simplicity.uat.circleone.com/graphql/',
  'www.prosper.com': 'https://simplicity.prosper.com/graphql/',
};

// gql queries that should always be called through the federation gateway
const federationGatewayPermanentQueries = [
  'createMarketingTouch',
  'createCreditCardApplication',
  'cardStarValidateCreditCardIncomeCollectionOutbound',
  'cardStarUpdateCreditCardIncomeCollection',
  'coreDashboardGetCreditCardAccount',
  'cardStarGetCreditCardAccountId',
];

const graphqlHostFromCurrentEnvironment = (queryName?: string) => {
  const { hostname } = window.location;
  let graphqlUrl = graphqlDomains[hostname];

  /**
   * If Gateway FF is enabled, and the specified query or mutation is enabled...
   * or if the query has been added to the permanent list, use the Gateway host
   */
  const useFederationGatewayHost =
    getFeatureVariable(
      GQL_FEDERATION_FEATURE_OPTIMIZELY_KEY,
      GQL_FEDERATION_QUERIES_AND_MUTATIONS_FEATURE_VARIABLE_OPTIMIZELY_KEY
    )?.list.includes(queryName) ||
    federationGatewayPermanentQueries.includes(queryName);

  if (useFederationGatewayHost) {
    // Federation gateway is deployed at /graphql on www.
    const { origin } = window.location;
    graphqlUrl = `${origin}/graphql`;

    // If local dev environment hit qa32
    if (origin.includes('localhost') || origin.includes('prosper.local')) {
      graphqlUrl = 'https://www.qa32.circleone.com/graphql';
    }
  }

  // ODE environment hostname has no value in the string map
  // TODO: May have to revisit as we use Gateway urls
  if (!graphqlUrl) {
    // Construct the graphQL URL for ODE
    return `https://${hostname.replace('www', 'simplicity')}/graphql/`;
  }

  return graphqlUrl;
};

// only use for footer because we cannot persist version for saving
export const getUrlForLegalDoc = (legalDocName: string) => {
  const host = graphqlHostFromCurrentEnvironment();
  const path = `credit-card-document-proxy/legal-documents/${legalDocName}`;
  return `${host}${path}`;
};

/// ///////////////////////////////
// Default Query Helper
/// ///////////////////////////////
// loginRedirect specifies if we should redirect to login in case of 401
export const query = async (
  queryName = '',
  queryString = '',
  variables = {},
  options = {},
  loginRedirect = true,
  sendAuthorizationHeader = true
) => {
  // point to the correct graphql host
  const graphqlHost = graphqlHostFromCurrentEnvironment(queryName);
  // gather the auth token
  const currentJWT = getJWTFromLocalStorage();
  // build the request
  const requestId = uuidv4(); // unique for each request
  const headers: any = {
    'Content-Type': 'application/json',
    'Client-Name': 'web-credit-card',
    'X-Correlation-ID': `${requestId}`,
    'apollographql-client-name': 'web-credit-card',
    'apollographql-client-version': '1.0',
    'prspr-q-id': queryName,
  };
  if (sendAuthorizationHeader && currentJWT) {
    headers.authorization = `bearer ${currentJWT}`;
  }
  const request = {
    method: 'POST',
    headers,
    body: JSON.stringify({
      operationName: queryName,
      query: queryString,
      variables,
      ...options,
    }),
  };
  // send the query
  let statusCode = 0;
  try {
    const response = await fetch(graphqlHost, request);
    statusCode = response.status;
    // handle any errors
    // parse the response
    if (response.status >= 200 && response.status < 300) {
      // success
      // but note that graphql responses may contain errors.
      const result = (await response.json()) as GraphQLResponse;
      // so check for graphql response errors and log them
      const graphqlErrors = result.errors;
      if (graphqlErrors) {
        const errorMessage =
          (graphqlErrors[0] && graphqlErrors[0].code) ?? 'GraphQL error';
        heapTrackClientError({
          errorName: 'GraphQLError',
          errorMessage,
          statusCode,
          endpoint: queryName,
          errorJson: JSON.stringify(graphqlErrors[0]),
          'X-Correlation-ID': `${requestId}`,
        });
      }
      // success, return results.
      return result;
    }
    // non-200 response, failure
    statusCode = response && response.status ? response.status : 0;
    switch (response && response.status) {
      case 504: {
        // throw timeout error
        throw new Error('Connection to server timed out');
      }
      case 524: {
        // throw cloudflare timeout error
        throw new Error('Connection between CloudFlare and server timed out');
      }
      case 401: {
        // construct a return url from the current url
        const returnURL = `${window.location.pathname}${window.location.search}`;
        // remove the invalid JWT
        removeJWTFromLocalStorage();
        // send them to the email-code sign in page
        if (loginRedirect) {
          window.location.assign(
            `/auth/email-code?redirect_uri=${encodeURIComponent(returnURL)}`
          );
        }
        // throw the unauth error so any consumer can react appropriately
        throw new Error(UNAUTHORIZED_MESSAGE);
      }
      case 403: {
        throw new Error('Request forbidden');
      }
      default: {
        // unknown, but error
        throw new Error('GraphQL error');
      }
    }
  } catch (error: any) {
    if (error.message !== UNAUTHORIZED_MESSAGE) {
      // Log unexpected errors to New Relic and Heap
      logError(error, {
        errorMessage: error.message,
        statusCode,
        endpoint: queryName,
        'X-Correlation-ID': `${requestId}`,
      });
    } else {
      // Track 401 Unauthorized with separate Heap event and skip New Relic
      heapTrackUnauthorized({
        errorMessage: error.message,
        statusCode,
        endpoint: queryName,
        'X-Correlation-ID': `${requestId}`,
      });
    }
    // send it up for the consumers to react to
    throw error;
  }
};
